import React from "react";
import PropTypes from "prop-types";
import { FormField, Input, Select, Toggle } from "../../../design-system";
import { textColors, typography } from "../../../design-system/tokens";

/**
 * Renders the parameter inputs of one condition from its `params_schema`
 * (website/automations/registry.py ParamSpec.to_dict()):
 *   int             → number input with min/max + help
 *   bool            → toggle
 *   str             → text input
 *   automation_slug → select over the existing automation slugs
 */

const rangeHint = (spec) => {
  const hasMin = spec.min !== null && spec.min !== undefined;
  const hasMax = spec.max !== null && spec.max !== undefined;
  if (hasMin && hasMax) return `${spec.min}–${spec.max}`;
  if (hasMin) return `min ${spec.min}`;
  if (hasMax) return `max ${spec.max}`;
  return "";
};

const helperFor = (spec) => {
  const parts = [];
  if (spec.help) parts.push(spec.help);
  const range = spec.type === "int" ? rangeHint(spec) : "";
  if (range) parts.push(`Allowed: ${range}.`);
  if (spec.default !== null && spec.default !== undefined && spec.type !== "bool") {
    parts.push(`Default: ${String(spec.default)}.`);
  }
  return parts.join(" ");
};

const ConditionParamFields = ({
  schema = [],
  values = {},
  onChange,
  automationSlugs = [],
  extraSlugOptions = [],
  idPrefix = "param",
  disabled = false,
  errors = {},
  className = "",
}) => {
  if (!Array.isArray(schema) || schema.length === 0) {
    return (
      <p className={`${typography.caption} ${textColors.muted} ${className}`}>This condition has no parameters.</p>
    );
  }

  const slugOptions = (() => {
    const seen = new Set();
    const out = [];
    const push = (slug, label) => {
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      out.push({ value: slug, label });
    };
    extraSlugOptions.forEach((o) => push(o.slug, o.label || o.slug));
    automationSlugs.forEach((a) => push(a.slug, a.name ? `${a.name} (${a.slug})` : a.slug));
    return out;
  })();

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${className}`}>
      {schema.map((spec) => {
        const id = `${idPrefix}-${spec.name}`;
        const value = values?.[spec.name];
        const error = errors?.[spec.name] || "";
        const label = spec.label || spec.name;

        if (spec.type === "bool") {
          return (
            <div key={spec.name} className="sm:col-span-2">
              <Toggle
                id={id}
                checked={Boolean(value)}
                onChange={(e) => onChange(spec.name, Boolean(e?.target?.checked))}
                label={label}
                description={spec.help || ""}
                labelPosition="right"
                size="sm"
                disabled={disabled}
              />
              {error ? (
                <p className={`mt-1 ${typography.caption} text-red-600 dark:text-red-400`} role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          );
        }

        if (spec.type === "automation_slug") {
          const current = value === null || value === undefined ? "" : String(value);
          const options = slugOptions.some((o) => o.value === current) || !current
            ? slugOptions
            : [...slugOptions, { value: current, label: `${current} (not found)` }];
          return (
            <FormField key={spec.name} label={label} htmlFor={id} required={Boolean(spec.required)}>
              <Select
                id={id}
                value={current}
                onChange={(e) => onChange(spec.name, e.target.value)}
                options={options}
                placeholder={options.length ? "Select an automation…" : "No automations yet"}
                disabled={disabled || options.length === 0}
                error={error}
                helperText={helperFor(spec)}
                size="sm"
              />
            </FormField>
          );
        }

        if (spec.type === "int") {
          return (
            <FormField key={spec.name} label={label} htmlFor={id} required={Boolean(spec.required)}>
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                value={value === null || value === undefined ? "" : String(value)}
                onChange={(e) => onChange(spec.name, e.target.value)}
                min={spec.min ?? undefined}
                max={spec.max ?? undefined}
                step={1}
                disabled={disabled}
                error={error}
                helperText={helperFor(spec)}
                placeholder={spec.default !== null && spec.default !== undefined ? String(spec.default) : ""}
                size="sm"
              />
            </FormField>
          );
        }

        return (
          <FormField key={spec.name} label={label} htmlFor={id} required={Boolean(spec.required)}>
            <Input
              id={id}
              type="text"
              value={value === null || value === undefined ? "" : String(value)}
              onChange={(e) => onChange(spec.name, e.target.value)}
              disabled={disabled}
              error={error}
              helperText={helperFor(spec)}
              size="sm"
            />
          </FormField>
        );
      })}
    </div>
  );
};

ConditionParamFields.propTypes = {
  schema: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string,
      label: PropTypes.string,
      help: PropTypes.string,
      default: PropTypes.any,
      min: PropTypes.number,
      max: PropTypes.number,
      required: PropTypes.bool,
    })
  ),
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  automationSlugs: PropTypes.arrayOf(
    PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string, id: PropTypes.number })
  ),
  extraSlugOptions: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string, label: PropTypes.string })),
  idPrefix: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  className: PropTypes.string,
};

export default ConditionParamFields;
